import { format } from "date-fns";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

interface AddOneTimeIncomeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAdd: (adjustment: any) => void;
}

const INCOME_CATEGORIES = ["Sales", "Refund", "Grant", "Investment", "Other"];

export function AddOneTimeIncomeModal({
  open,
  onOpenChange,
  onAdd,
}: AddOneTimeIncomeModalProps) {
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState<Date>();
  const [category, setCategory] = useState("");

  const amountNum = parseFloat(amount) || 0;
  const isValid = description && amountNum > 0 && date && category;

  useEffect(() => {
    if (!open) {
      setDescription("");
      setAmount("");
      setDate(undefined);
      setCategory("");
    }
  }, [open]);

  const handleAdd = () => {
    if (!isValid) return;

    onAdd({
      type: "one-time-income",
      description: description,
      amount: amountNum,
      date: date,
      category: category,
      impact: `One-time income of £${(amountNum / 1000).toFixed(1)}K on ${format(date!, "MMM dd, yyyy")}`,
    });

    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add One-Time Income</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Description */}
          <div className="space-y-2">
            <Label>Description *</Label>
            <Textarea
              placeholder="e.g., Government grant received"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
            />
          </div>

          {/* Amount */}
          <div className="space-y-2">
            <Label>Amount (£) *</Label>
            <Input
              type="number"
              placeholder="0.00"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              min="0"
              step="0.01"
            />
          </div>

          {/* Date */}
          <div className="space-y-2">
            <Label>Date (when received) *</Label>
            <DatePicker
              value={date}
              onChange={setDate}
              dateFormat="MMM dd, yyyy"
              placeholder="Pick a date"
            />
          </div>

          {/* Category */}
          <div className="space-y-2">
            <Label>Category *</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger>
                <SelectValue placeholder="Select category..." />
              </SelectTrigger>
              <SelectContent>
                {INCOME_CATEGORIES.map((cat) => (
                  <SelectItem key={cat} value={cat}>
                    {cat}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Impact Preview */}
          {isValid && (
            <div className="border-t pt-4 space-y-2">
              <p className="text-sm font-medium">Impact Preview</p>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span>Cash impact on {format(date!, "MMM dd")}:</span>
                  <span className="text-green-500">
                    +£{amountNum.toLocaleString()}
                  </span>
                </div>
                <div className="pt-2 border-t">
                  <p className="text-muted-foreground">
                    Net effect: One-time income of £
                    {(amountNum / 1000).toFixed(1)}K
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handleAdd} disabled={!isValid}>
              Add Adjustment
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
